// Merkan · Dokumente hochladen und per KI auslesen (Rechnung, Angebot, Kontoauszug aus PDF, Scan, Foto oder XML)
import { dokumentZeigen } from './ansicht';
import { supabase } from './supabase';
import { ki } from './ki';
import { t } from './sprache';
import { pdfBase64 } from './pdf';

export interface DokPartei { name: string; strasse: string; plz: string; ort: string; land: string; ust_id: string; email: string; telefon: string }
export interface DokPosition {
  pos: string; artikelnummer: string; hersteller: string; bezeichnung: string; menge: number; einheit: string; einzelpreis: number; gesamt: number;
  ust_satz: number; lieferzeit_tage: number; nummern?: string[]; menge_stueck?: number; bezeichnung_de?: string; liefertermin?: string; artikel_id?: string; artikel_nr?: string; artikel_bezeichnung?: string; artikel_sicherheit?: number;
}
export interface DokBuchung { datum: string; betrag: number; name: string; verwendungszweck: string; iban: string }
export interface DokErgebnis {
  art: 'rechnung' | 'gutschrift' | 'angebot' | 'auftragsbestaetigung' | 'bestellung' | 'kontoauszug' | 'lieferschein' | 'sonstiges';
  sicherheit: number; aussteller: DokPartei; empfaenger: DokPartei; nummer: string; datum: string; leistungsdatum: string; faellig_am: string;
  waehrung: string; summe_netto: number; summe_ust: number; summe_brutto: number; zahlbetrag: number; ust_saetze: { satz: number; netto: number; ust: number }[];
  iban: string; bic: string; verwendungszweck: string; referenzen: string[]; zahlungsbedingung: string; skonto_prozent: number; skonto_tage: number;
  gueltig_bis: string; positionen: DokPosition[]; buchungen: DokBuchung[]; hinweise: string[]; lieferant_id: string | null; xml: boolean; lauf_id?: string | null;
  /** Nur Kundenauftrag (Quereinstieg): Kunde, Bestellnummer, Schiff und Treffer im Stamm */
  kunde?: DokPartei; bestellnummer?: string; schiff_name?: string; schiff_imo?: string; betreff?: string; lieferadresse?: string; liefertermin?: string;
  kunde_id?: string | null; schiff_id?: string | null;
}
export type DokArt = 'rechnung' | 'angebot' | 'kontoauszug' | 'lieferschein' | 'kundenauftrag' | 'auto';

export const DOK_ANNAHME = '.pdf,.jpg,.jpeg,.png,.webp,.xml,application/pdf,image/jpeg,image/png,image/webp,text/xml,application/xml';
const sicher = (s: string) => s.replace(/[^\p{L}\p{N}._ -]+/gu, '_').slice(0, 120) || 'datei';

/** Datei in den Speicher des Mandanten legen; liefert den Pfad */
export async function dokumentHochladen(mandantId: string, datei: File, ordner: string): Promise<string> {
  if (datei.size > 20 * 1024 * 1024) throw new Error(t('Die Datei ist größer als 20 MB.'));
  const pfad = `${mandantId}/${ordner}/${crypto.randomUUID().slice(0, 8)}_${sicher(datei.name)}`;
  const { error } = await supabase.storage.from('dateien').upload(pfad, datei, { contentType: datei.type || undefined, upsert: false });
  if (error) throw new Error(error.message);
  return pfad;
}

const alsDataUrl = (f: Blob) => new Promise<string>((ok, fehler) => { const r = new FileReader(); r.onload = () => ok(String(r.result)); r.onerror = () => fehler(r.error); r.readAsDataURL(f); });
const bildMasse = (url: string) => new Promise<{ w: number; h: number }>((ok, fehler) => { const i = new Image(); i.onload = () => ok({ w: i.naturalWidth, h: i.naturalHeight }); i.onerror = fehler; i.src = url; });

/** Foto verkleinern (lange Kante höchstens 2400 px, JPEG): Handyfotos haben oft 4 bis 8 MB, die KI nimmt höchstens 5 MB je Bild */
async function bildVerkleinern(f: File, max = 2400): Promise<{ datei: File; dataUrl: string }> {
  const url = await alsDataUrl(f);
  const { w, h } = await bildMasse(url);
  const faktor = Math.min(1, max / Math.max(w, h));
  if (faktor === 1 && /jpeg/i.test(f.type) && f.size < 3_500_000) return { datei: f, dataUrl: url };
  const c = document.createElement('canvas'); c.width = Math.round(w * faktor); c.height = Math.round(h * faktor);
  const i = new Image(); i.src = url; await i.decode();
  const g = c.getContext('2d')!; g.fillStyle = '#fff'; g.fillRect(0, 0, c.width, c.height); g.drawImage(i, 0, 0, c.width, c.height);
  const dataUrl = c.toDataURL('image/jpeg', 0.88);
  const bytes = Uint8Array.from(atob(dataUrl.split(',')[1]), (x) => x.charCodeAt(0));
  return { datei: new File([bytes], f.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' }), dataUrl };
}

export interface DokVorbereitet { datei: File; seiten: { inhalt: string; typ: string; name: string }[] | null }

/** Auswahl für Speicher und KI vorbereiten: Fotos verkleinern; mehrere Fotos einer Rechnung werden als ein PDF gespeichert,
 *  die KI bekommt die Seiten aber als einzelne Bilder (in voller Auflösung zuverlässiger als ein PDF aus Bildern) */
export async function dokumentVorbereiten(dateien: File[]): Promise<DokVorbereitet> {
  const bild = (f: File) => /^image\/(jpeg|png|webp)$/i.test(f.type);
  if (dateien.length === 1) return { datei: bild(dateien[0]) ? (await bildVerkleinern(dateien[0])).datei : dateien[0], seiten: null };
  if (!dateien.every(bild)) throw new Error(t('Mehrere Dateien nur als Fotos (JPG, PNG) einer Rechnung; ein PDF bitte einzeln hochladen.'));
  if (dateien.length > 10) throw new Error(t('Höchstens 10 Seiten auf einmal.'));
  const klein = [];
  for (const f of dateien) klein.push(await bildVerkleinern(f));
  const b64 = await pdfBase64({ pageSize: 'A4', pageMargins: [20, 20, 20, 20], content: klein.map((k, i) => ({ image: k.dataUrl, fit: [555, 802], alignment: 'center', ...(i ? { pageBreak: 'before' } : {}) })) });
  const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  const name = dateien[0].name.replace(/\.[^.]+$/, '') + `_${dateien.length}_Seiten.pdf`;
  return {
    datei: new File([bytes], name, { type: 'application/pdf' }),
    seiten: klein.map((k, i) => ({ inhalt: k.dataUrl.split(',')[1], typ: 'image/jpeg', name: `seite_${i + 1}.jpg` })),
  };
}

// ---------------------------------------------------------------------------
// Lesestand für die Anzeige während des Lesens (eine Anzeige für alle Seiten, siehe ui/dokument-lesen.tsx)
// ---------------------------------------------------------------------------
export type LesePhase = 'senden' | 'ki' | 'pruefen' | 'fertig';
export interface LeseStand {
  id: number; art: DokArt; name: string; seiten: number; xml: boolean; phase: LesePhase; start: number; kiStart: number | null;
  text: string; schritte: { schritt: string; ergebnis: string; ms: number }[]; hintergrund: boolean;
}
let leseStand: LeseStand | null = null;
let leseZaehler = 0;
let geplant = false;
const leseHoerer = new Set<() => void>();
const benachrichtigen = () => {
  if (geplant) return;
  geplant = true;
  requestAnimationFrame(() => { geplant = false; leseHoerer.forEach((f) => f()); });
};
export const lesen = {
  stand: () => leseStand,
  abonnieren: (f: () => void) => { leseHoerer.add(f); return () => { leseHoerer.delete(f); }; },
  aendern(p: Partial<LeseStand>, id?: number) { if (leseStand && (id == null || leseStand.id === id)) { leseStand = { ...leseStand, ...p }; benachrichtigen(); } },
  starten(p: Pick<LeseStand, 'art' | 'name' | 'seiten' | 'xml'>): number {
    leseStand = { ...p, id: ++leseZaehler, phase: 'senden', start: Date.now(), kiStart: null, text: '', schritte: [], hintergrund: leseStand?.hintergrund ?? false };
    benachrichtigen();
    return leseStand.id;
  },
  beenden(id: number, ok: boolean) {
    if (!leseStand || leseStand.id !== id) return;
    if (!ok) { leseStand = null; benachrichtigen(); return; }
    leseStand = { ...leseStand, phase: 'fertig' };
    benachrichtigen();
    setTimeout(() => { if (leseStand?.id === id) { leseStand = null; benachrichtigen(); } }, 700);
  },
  inHintergrund(an: boolean) { if (leseStand) { leseStand = { ...leseStand, hintergrund: an }; benachrichtigen(); } },
};

const FUNKTIONS_URL = `${import.meta.env.VITE_SUPABASE_URL as string}/functions/v1/ki`;
const OEFFENTLICHER_SCHLUESSEL = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

/** Dokumenterkennung als Strom: Schritte und Teilergebnisse gehen sofort in den Lesestand */
async function stromLesen(mandantId: string, daten: Record<string, unknown>, id: number): Promise<{ ok: true; ergebnis: DokErgebnis } | { ok: false; hinweis: string }> {
  const { data: s } = await supabase.auth.getSession();
  let r: Response;
  try {
    r = await fetch(FUNKTIONS_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json', apikey: OEFFENTLICHER_SCHLUESSEL, Authorization: `Bearer ${s.session?.access_token ?? ''}` },
      body: JSON.stringify({ aktion: 'dokument', mandant_id: mandantId, strom: true, ...daten }),
    });
  } catch {
    r = new Response(null, { status: 503 });
  }
  // Kaltstart oder Gateway: wie bisher ohne Strom (mit einer Wiederholung)
  if ([502, 503, 504].includes(r.status)) {
    const k = await ki<DokErgebnis>(mandantId, 'dokument', daten);
    return k.ok && k.ergebnis ? { ok: true, ergebnis: k.ergebnis } : { ok: false, hinweis: k.hinweis ?? t('Das Dokument konnte nicht ausgelesen werden.') };
  }
  if (!(r.headers.get('content-type') ?? '').includes('text/event-stream') || !r.body) {
    const j = await r.json().catch(() => ({})) as { ok?: boolean; ergebnis?: DokErgebnis; hinweis?: string };
    return j.ok && j.ergebnis ? { ok: true, ergebnis: j.ergebnis } : { ok: false, hinweis: j.hinweis ?? t('Das Dokument konnte nicht ausgelesen werden.') };
  }
  const leser = r.body.pipeThrough(new TextDecoderStream()).getReader();
  let puffer = '';
  let text = '';
  const schritte: LeseStand['schritte'] = [];
  try {
    for (;;) {
      const { value, done } = await leser.read();
      if (done) break;
      puffer += value;
      let p: number;
      while ((p = puffer.indexOf('\n\n')) >= 0) {
        const roh = puffer.slice(0, p).replace(/^data:\s?/, '');
        puffer = puffer.slice(p + 2);
        let e: Record<string, any>;
        try { e = JSON.parse(roh); } catch { continue; }
        if (e.t === 'teil') { text += e.d; lesen.aendern({ text }, id); }
        else if (e.t === 'schritt') { schritte.push({ schritt: e.schritt, ergebnis: e.ergebnis, ms: e.ms }); lesen.aendern({ schritte: [...schritte] }, id); }
        else if (e.t === 'phase' && e.phase === 'ki') lesen.aendern({ phase: 'ki', kiStart: Date.now() }, id);
        else if (e.t === 'phase' && e.phase === 'pruefen') lesen.aendern({ phase: 'pruefen' }, id);
        else if (e.t === 'ergebnis') return { ok: true, ergebnis: e.ergebnis as DokErgebnis };
        else if (e.t === 'fehler') return { ok: false, hinweis: String(e.hinweis ?? t('Das Dokument konnte nicht ausgelesen werden.')) };
      }
    }
  } catch {
    return { ok: false, hinweis: t('Die Verbindung zu {ki} wurde unterbrochen. Bitte erneut versuchen.') };
  }
  return { ok: false, hinweis: t('Die Verbindung zu {ki} wurde unterbrochen. Bitte erneut versuchen.') };
}

/** E-Rechnung im PDF (ZUGFeRD, Factur-X, XRechnung als Anhang): eingebettetes XML suchen, damit die Edge Function es
 *  direkt liest (eRechnungLesen) statt das Bild per KI. Ohne Treffer oder bei Fehlern null, dann liest die KI das PDF. */
async function eingebettetesXmlSuchen(pfad: string, quelle?: Blob | null): Promise<{ inhalt: string; typ: string; name: string } | null> {
  try {
    let blob = quelle ?? null;
    if (!blob) {
      const { data, error } = await supabase.storage.from('dateien').download(pfad);
      if (error || !data) return null;
      blob = data;
    }
    if (blob.size > 25 * 1024 * 1024) return null;
    const { eingebetteteERechnung, textBase64 } = await import('../seiten/rw/erechnung');
    const x = await eingebetteteERechnung(new Uint8Array(await blob.arrayBuffer()));
    return x ? { inhalt: textBase64(x.xml), typ: 'application/xml', name: x.name } : null;
  } catch {
    return null;
  }
}

/** Gespeicherte Datei (Upload oder Mailanhang) per KI auslesen; mit seiten werden stattdessen die einzelnen Seitenbilder gelesen.
 *  Rechnungen im PDF mit eingebetteter E-Rechnung werden direkt aus dem XML gelesen (quelle: die hochgeladene Datei, sonst
 *  wird sie aus dem Speicher geladen). Während des Lesens zeigt die Anzeige (ui/dokument-lesen.tsx) Fortschritt und erste Ergebnisse. */
export async function dokumentAuslesen(mandantId: string, pfad: string, art: DokArt, typ?: string | null, seiten?: DokVorbereitet['seiten'], quelle?: Blob | null): Promise<{ ok: true; ergebnis: DokErgebnis } | { ok: false; hinweis: string }> {
  const istPdf = /pdf/i.test(typ ?? '') || /\.pdf$/i.test(pfad);
  if (!seiten?.length && istPdf && (art === 'rechnung' || art === 'auto')) {
    const x = await eingebettetesXmlSuchen(pfad, quelle);
    if (x) seiten = [x];
  }
  const eingebettet = Boolean(seiten?.length === 1 && seiten[0].typ === 'application/xml' && istPdf);
  const name = eingebettet ? `${(pfad.split('/').pop() ?? '').replace(/^[0-9a-f]{8}_/, '')} (${t('E-Rechnung im PDF')})` : seiten?.length ? `${seiten.length} ${t('Seiten')}` : (pfad.split('/').pop() ?? '').replace(/^[0-9a-f]{8}_/, '');
  const id = lesen.starten({ art, name, seiten: seiten?.length || 1, xml: eingebettet || /xml/i.test(typ ?? '') || /\.xml$/i.test(pfad) });
  try {
    const r = await stromLesen(mandantId, seiten?.length ? { art, dateien: seiten, pfad } : { pfad, art, datei: { name: pfad.split('/').pop(), typ: typ ?? '' } }, id);
    lesen.beenden(id, r.ok);
    return r;
  } catch {
    lesen.beenden(id, false);
    return { ok: false, hinweis: t('Das Dokument konnte nicht ausgelesen werden.') };
  }
}

/** Teilergebnis aus noch unvollständigem JSON der KI: einzelne Felder und bereits vollständige Listeneinträge */
export function teilErgebnis(text: string): { aussteller: string; nummer: string; datum: string; brutto: number | null; positionen: Partial<DokPosition>[]; buchungen: Partial<DokBuchung>[]; art: string } {
  const zeichen = (re: RegExp) => { const m = text.match(re); if (!m) return ''; try { return JSON.parse(`"${m[1]}"`) as string; } catch { return m[1]; } };
  const s = (name: string) => zeichen(new RegExp(`"${name}"\\s*:\\s*"((?:[^"\\\\]|\\\\.)*)"`));
  const zahlM = text.match(/"summe_brutto"\s*:\s*(-?\d+(?:\.\d+)?)\s*[,}]/);
  const liste = (name: string) => {
    const aus: unknown[] = [];
    const i = text.indexOf(`"${name}"`);
    if (i < 0) return aus;
    const a = text.indexOf('[', i);
    if (a < 0) return aus;
    let tiefe = 0, inText = false, flucht = false, anfang = -1;
    for (let j = a + 1; j < text.length; j++) {
      const c = text[j];
      if (inText) { if (flucht) flucht = false; else if (c === '\\') flucht = true; else if (c === '"') inText = false; continue; }
      if (c === '"') inText = true;
      else if (c === '{') { if (!tiefe) anfang = j; tiefe++; }
      else if (c === '}') { tiefe--; if (!tiefe && anfang >= 0) { try { aus.push(JSON.parse(text.slice(anfang, j + 1))); } catch { /* unvollständig */ } anfang = -1; } }
      else if (c === ']' && !tiefe) break;
    }
    return aus;
  };
  return {
    art: s('art'),
    aussteller: zeichen(/"aussteller"\s*:\s*\{\s*"name"\s*:\s*"((?:[^"\\]|\\.)*)"/),
    nummer: s('nummer'), datum: s('datum'),
    brutto: zahlM ? Number(zahlM[1]) : null,
    positionen: liste('positionen') as Partial<DokPosition>[],
    buchungen: liste('buchungen') as Partial<DokBuchung>[],
  };
}

/** Gespeicherte Datei (Bucket dateien) direkt in der App ansehen */
export function dateiZeigen(pfad: string, dateiname: string, typ?: string | null, titel?: string): void {
  dokumentZeigen({
    titel: titel ?? dateiname, dateiname, typ,
    laden: async () => {
      const { data, error } = await supabase.storage.from('dateien').download(pfad);
      if (error || !data) throw new Error(t('Die Datei konnte nicht geladen werden.'));
      return data;
    },
  });
}

/** Kurzlink zum Ansehen einer gespeicherten Datei (10 Minuten gültig) */
export async function dokumentLink(pfad: string): Promise<string | null> {
  const { data } = await supabase.storage.from('dateien').createSignedUrl(pfad, 600);
  return data?.signedUrl ?? null;
}

export const DOK_ART_NAME: Record<string, string> = {
  rechnung: t('Rechnung'), gutschrift: t('Gutschrift'), angebot: t('Angebot'), auftragsbestaetigung: t('Auftragsbestätigung'), bestellung: t('Bestellung des Kunden'),
  kontoauszug: t('Kontoauszug'), lieferschein: t('Lieferschein'), sonstiges: t('Sonstiges Dokument'),
};

/** Abweichung zwischen KI-Ergebnis und dem, was der Mensch gespeichert hat, am Lauf vermerken (Lernsignal) */
export async function korrekturMelden(laufId: string | null | undefined, ki: Record<string, unknown>, gespeichert: Record<string, unknown>): Promise<void> {
  if (!laufId) return;
  const norm = (v: unknown) => (typeof v === 'number' ? Math.round(v * 100) / 100 : typeof v === 'string' ? v.trim().replace(/\s+/g, ' ') : v ?? null);
  const abweichung: Record<string, { ki: unknown; mensch: unknown }> = {};
  for (const k of Object.keys(gespeichert)) {
    const a = norm(ki[k]), b = norm(gespeichert[k]);
    if (JSON.stringify(a ?? '') !== JSON.stringify(b ?? '')) abweichung[k] = { ki: a, mensch: b };
  }
  await supabase.rpc('ki_lauf_korrektur', { p_lauf: laufId, p_korrektur: { felder: abweichung, unveraendert: !Object.keys(abweichung).length } });
}
